import { useEffect, useRef } from 'react'

/**
 * Ширина зоны у левого края, с которой начинается жест. Как в системных
 * жестах iOS и Android: приложение полно горизонтальных слайдеров и лент
 * (баннер, слайдер картинок в рецепте, ряды категорий), и свайп по всему
 * экрану воевал бы с ними за один и тот же жест.
 */
const EDGE_ZONE_PX = 32
/** Насколько нужно утянуть, чтобы жест сработал. */
const COMPLETE_PX = 90
/** Скорость «броска» (px/ms), при которой возвращаемся без длинного свайпа. */
const COMPLETE_VELOCITY = 0.5
/** Вертикальное движение больше этого — считаем, что человек скроллит. */
const VERTICAL_CANCEL_PX = 12
/** Горизонтальное движение, после которого жест считается начатым. */
const HORIZONTAL_START_PX = 8

interface UseSwipeBackOptions {
  enabled: boolean
  onBack: () => void
}

/** Открыт оверлей (истории, модалка) — жест навигации ему только мешает. */
const hasOpenOverlay = () => document.querySelector('[role="dialog"]') !== null

/**
 * Возврат назад свайпом от левого края экрана.
 *
 * Слушаем на этапе перехвата и на старте жеста гасим всплытие: иначе тот же
 * свайп поймает слайдер картинок в карточке рецепта и пролистает изображение
 * вместо возврата. Нативный скролл это не ломает — его блокирует только
 * preventDefault, который зовём лишь после того, как жест признан
 * горизонтальным.
 *
 * @param enabled - включён ли жест на текущем экране
 * @param onBack - что делать при завершённом жесте
 */
export const useSwipeBack = ({ enabled, onBack }: UseSwipeBackOptions) => {
  // Колбэк держим в ref: иначе слушатели переподписывались бы на каждый рендер.
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  useEffect(() => {
    if (!enabled) return

    let startX = 0
    let startY = 0
    let startedAt = 0
    let tracking = false
    let active = false

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || hasOpenOverlay()) return
      const t = e.touches[0]
      if (t.clientX > EDGE_ZONE_PX) return

      startX = t.clientX
      startY = t.clientY
      startedAt = performance.now()
      tracking = true
      active = false
      // Слайдеры реагируют именно на touchstart — не даём событию дойти до них.
      e.stopPropagation()
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking) return
      const t = e.touches[0]
      const dx = t.clientX - startX
      const dy = t.clientY - startY

      if (!active) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > VERTICAL_CANCEL_PX) {
          tracking = false
          return
        }
        if (dx < HORIZONTAL_START_PX) return
        active = true
      }

      e.stopPropagation()
      // Глушим горизонтальный оверскролл, пока ведём жест.
      if (e.cancelable) e.preventDefault()
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return
      const wasActive = active
      tracking = false
      active = false
      if (!wasActive) return

      const dx = (e.changedTouches[0]?.clientX ?? startX) - startX
      const velocity = dx / Math.max(1, performance.now() - startedAt)
      if (dx > COMPLETE_PX || velocity > COMPLETE_VELOCITY) {
        onBackRef.current()
      }
    }

    const opts: AddEventListenerOptions = { capture: true, passive: false }
    document.addEventListener('touchstart', onTouchStart, opts)
    document.addEventListener('touchmove', onTouchMove, opts)
    document.addEventListener('touchend', onTouchEnd, opts)
    document.addEventListener('touchcancel', onTouchEnd, opts)

    return () => {
      document.removeEventListener('touchstart', onTouchStart, opts)
      document.removeEventListener('touchmove', onTouchMove, opts)
      document.removeEventListener('touchend', onTouchEnd, opts)
      document.removeEventListener('touchcancel', onTouchEnd, opts)
    }
  }, [enabled])
}
